import React, { useState } from "react";
import { Mail, Copy, Check, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, logEmailEnquiry } from "@/lib/api";
import { useT } from "@/lib/i18n";

/*
  Click-to-Email floating button.
  - Positioned above the WhatsApp float (which is at bottom-6). This sits at bottom-24.
  - Follows the exact WhatsAppFloat architecture: primary <a> + small "Options" button + a popover.
  - Generates a unique Query ID (ZOP-YYYYMMDD-XXXXXX) per click, persists it to the backend
    (fire-and-forget, never blocks) and opens the user's default mail client with the
    prefilled subject + body.
*/

const RECIPIENT = "sales@zopran.com";

/* ================ Query ID + mailto helpers ================ */

export function generateQueryId() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  const date = `${y}${m}${d}`;
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no ambiguous chars
  let suffix = "";
  const cryptoObj = typeof window !== "undefined" ? window.crypto || window.msCrypto : null;
  if (cryptoObj && cryptoObj.getRandomValues) {
    const arr = new Uint32Array(6);
    cryptoObj.getRandomValues(arr);
    for (let i = 0; i < 6; i++) suffix += alphabet[arr[i] % alphabet.length];
  } else {
    for (let i = 0; i < 6; i++) suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `ZOP-${date}-${suffix}`;
}

function buildBody(queryId) {
  // Keep line breaks — mailto browsers preserve %0A / %0D%0A.
  return [
    "Dear Zopran Team,",
    "",
    "I would like to submit an enquiry regarding your logistics and customs services.",
    "",
    `Query ID: ${queryId}`,
    "",
    "Company Name:",
    "Contact Person:",
    "Telephone Number:",
    "Service Required:",
    "Origin:",
    "Destination:",
    "Commodity / Product:",
    "Number of Packages:",
    "Gross Weight:",
    "Dimensions:",
    "Incoterm:",
    "Preferred Mode of Transport:",
    "Requested Collection Date:",
    "Additional Information / Enquiry Details:",
    "",
    "Kind regards,",
    "[Your Name]",
  ].join("\r\n");
}

export function buildMailto(queryId) {
  const subject = `New Website Enquiry | Query ID: ${queryId}`;
  const body = buildBody(queryId);
  return `mailto:${RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Programmatic launcher — generates a fresh Query ID, persists it, opens mail client.
 * Returns the query id so callers can display it if needed.
 */
export function openEmailEnquiry() {
  const queryId = generateQueryId();
  logEmailEnquiry(queryId); // fire-and-forget
  const href = buildMailto(queryId);
  try {
    window.location.href = href;
  } catch {
    /* no-op */
  }
  return queryId;
}

/* ================ Floating component ================ */

export default function EmailFloat() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [queryId, setQueryId] = useState(() => generateQueryId());

  const href = buildMailto(queryId);

  const onPrimary = (e) => {
    // Regenerate a new Query ID so every fresh click is unique, then re-target and let the anchor navigate.
    const fresh = generateQueryId();
    setQueryId(fresh);
    logEmailEnquiry(fresh);
    e.currentTarget.setAttribute("href", buildMailto(fresh));
    toast.success(`Query ID ${fresh} generated. Opening your email…`);
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(queryId);
      setCopied(true);
      toast.success(`Query ID ${queryId} copied.`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(`Query ID: ${queryId}`);
    }
  };

  return (
    <div
      data-testid="email-float-dock"
      className="fixed right-6 bottom-32 z-40 flex flex-col items-end gap-3"
    >
      {open && (
        <div data-testid="email-popover" className="zp-card p-4 w-72 shadow-xl">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="zp-label">{t("common.emailEnquiry")}</div>
              <div className="font-display tracking-tight mt-1 text-[15px]">{RECIPIENT}</div>
              <div className="text-[11px] text-[color:var(--zp-text-3)] mt-1">
                Query ID · <span data-testid="email-query-id" className="font-mono text-[color:var(--zp-navy)]">{queryId}</span>
              </div>
            </div>
            <button
              data-testid="email-close"
              onClick={() => setOpen(false)}
              className="w-8 h-8 grid place-items-center rounded-full border border-[color:var(--zp-border)] text-[color:var(--zp-text-2)] hover:bg-[color:var(--zp-bg-2)]"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex flex-col gap-2">
            <a
              data-testid="email-open-mailto"
              href={href}
              onClick={onPrimary}
              className="zp-btn-cyan text-sm"
            >
              <Mail size={14} /> Open my mail app <ExternalLink size={12} />
            </a>
            <button data-testid="email-copy-id" onClick={copyId} className="zp-btn-ghost text-sm">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy Query ID"}
            </button>
            <button
              data-testid="email-regen"
              onClick={() => setQueryId(generateQueryId())}
              className="text-[11px] uppercase tracking-[0.18em] font-bold text-[color:var(--zp-text-3)] hover:text-[color:var(--zp-navy)] mt-1"
            >
              Regenerate ID
            </button>
          </div>
        </div>
      )}

      {/* Primary anchor — opens the user's default mail app immediately */}
      <a
        data-testid="email-float"
        href={href}
        onClick={onPrimary}
        onContextMenu={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        aria-label={t("common.emailEnquiry")}
        title={t("common.emailTooltip")}
        className="group relative"
      >
        <span className="relative inline-flex">
          <span
            className="relative w-14 h-14 grid place-items-center rounded-full text-white shadow-[0_12px_36px_rgba(10,37,64,0.35)] border-2 border-white transition-transform group-hover:scale-105 focus-visible:ring-2 focus-visible:ring-[color:var(--zp-cyan-2)]"
            style={{ background: "linear-gradient(180deg,#14365A 0%,#0A2540 100%)" }}
          >
            <Mail size={22} strokeWidth={1.9} />
          </span>
        </span>
      </a>

      {/* Small overflow button that mirrors the WhatsApp "Options" trigger */}
      <button
        data-testid="email-more"
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        aria-label="Email options"
        className="text-[10px] uppercase tracking-[0.18em] font-bold text-[color:var(--zp-text-2)] bg-white border border-[color:var(--zp-border)] rounded-full px-3 py-1 shadow-sm hover:bg-[color:var(--zp-bg-2)]"
      >
        Options
      </button>
    </div>
  );
}
